import React, { useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import './LegalPage.css';

function TermsOfService({ user, signIn }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="legal-page">
      <Header user={user} signIn={signIn} />
      <main className="legal-content" data-testid="tos-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: August 6, 2026</p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service ("Terms") govern your access to and use of Tweedia
          Studio and any related services, applications, and websites (collectively,
          "the Service"), operated by Tweedia Technologies LLC ("Tweedia," "we," "us," or
          "our"). These Terms constitute a legally binding agreement between you and
          Tweedia Technologies LLC.
        </p>
        <p>
          You accept these Terms by explicitly agreeing to them through the in-app
          acceptance prompt presented after you sign in to the Service. If you do not
          accept these Terms when prompted, you will not be able to use the Service.
          You may browse public pages of the Service (such as the landing page, these
          Terms, and our Privacy Policy) without accepting, but account-protected
          features require acceptance.
        </p>

        <h2>2. Description of Service</h2>
        <p>
          Tweedia Studio is a web-based video editing platform that analyzes
          long-duration footage and uses AI-powered algorithms to automatically
          detect and remove stagnant moments, producing shorter, more engaging
          timelapse videos. Features, functionality, and pricing may change at any
          time with or without notice.
        </p>

        <h2>3. Accounts</h2>
        <p>
          To use certain features of the Service, you must create an account using
          Google Sign-In or another authentication method we offer. You are
          responsible for safeguarding your account credentials and for all activity
          that occurs under your account. You must be at least 13 years of age to
          create an account and use the Service. If you are under 18, you represent
          that your parent or legal guardian has reviewed and agreed to these Terms
          on your behalf.
        </p>

        <h2>4. User Content</h2>

        <h3>4.1 Ownership</h3>
        <p>
          You retain all ownership rights in the video content, images, audio, and
          other materials you upload to the Service ("User Content"). Nothing in these
          Terms transfers ownership of your User Content to Tweedia.
        </p>

        <h3>4.2 Service License</h3>
        <p>
          By uploading User Content, you grant Tweedia Technologies LLC a limited,
          non-exclusive, worldwide, royalty-free license to use, reproduce, process,
          store, and transform your User Content solely for the purpose of providing,
          operating, and maintaining the Service for you. This license terminates when
          you delete the applicable User Content or your account, except as necessary
          for backup, archival, or legal purposes.
        </p>

        <h3>4.3 Machine Learning Training License</h3>
        <p>
          With your explicit consent (indicated via the consent mechanism presented
          after sign up), you grant Tweedia Technologies LLC a worldwide, non-exclusive,
          irrevocable, perpetual, royalty-free, fully paid, transferable, and
          sublicensable license to use, reproduce, modify, adapt, process, and create
          derivative works from your User Content for the purposes of:
        </p>
        <ol type="a">
          <li>
            Training, developing, and improving machine learning models, artificial
            intelligence systems, and related technologies;
          </li>
          <li>
            Research and development of video processing, computer vision, and content
            analysis capabilities;
          </li>
          <li>
            Developing new products, features, and services powered by such models
            and technologies.
          </li>
        </ol>
        <p>
          This ML Training License survives termination or deletion of your account.
          Once content has been incorporated into a trained model, individual
          contributions cannot be extracted or removed from that model. You may
          withdraw consent for future ML training use at any time by toggling the
          "AI &amp; Machine Learning Training" setting in the{' '}
          <strong>Privacy &amp; Data</strong> section of your{' '}
          <a href="/account">Account Settings</a>, or by contacting{' '}
          <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a> (see our{' '}
          <a href="/privacy">Privacy Policy</a>, Section 6.4 for details).
        </p>
        <p>
          <strong>This consent is optional.</strong> You are not required to consent
          to ML training use in order to access or use any features of the Service.
          You may manage your consent at any time from the Privacy &amp; Data section
          of your <a href="/account">Account Settings</a>.
        </p>

        <h3>4.4 Representations and Warranties</h3>
        <p>
          You represent and warrant that: (a) you own or have all necessary rights,
          licenses, and permissions to upload your User Content and grant the licenses
          described in this Section 4; (b) your User Content does not infringe,
          misappropriate, or violate any third party's intellectual property rights,
          privacy rights, publicity rights, or other legal rights; and (c) your User
          Content does not violate any applicable law or regulation.
        </p>

        <h3>4.5 Storage, Quotas, and Retention</h3>
        <p>
          The Service provides working storage for the content you upload; it is not a
          permanent backup or archive. You are responsible for keeping your own copies
          of your original files.
        </p>
        <p>
          <strong>Pooled storage allowance.</strong> The amount of data you may store is
          governed by the pooled cloud storage allowance included with your plan, shared
          across all of your projects, as described in Section 6.5. The allowance counts
          everything you keep in your account &mdash; uploaded photos, source videos, and
          the videos you render &mdash; and it is the only storage limit that applies.
          Once you reach your plan's allowance, further uploads are declined until you
          free up space or upgrade; we do not delete your existing content to make room.
          Projects themselves are unlimited in number, and there is no per-project photo
          or storage ceiling. We may change the allowance with reasonable notice.
        </p>
        <p>
          <strong>Raw photo retention.</strong> How long we keep the raw source photos
          you upload (and the thumbnails generated from them) depends on your plan:
        </p>
        <ul>
          <li>
            <strong>Free plan.</strong> Raw source photos are removed from active
            storage 30 days after a project's last activity, where "activity" means
            uploading photos to, or generating a video from, that project. We will send
            an advance warning email to the address associated with your account before
            this removal occurs, so you have an opportunity to preserve them (for
            example, by adding a photo or re-rendering, which resets the clock, or by
            upgrading to a paid plan).
          </li>
          <li>
            <strong>Paid subscription plans.</strong> While your subscription is active
            and in good standing, your raw source photos are retained and are not
            subject to the 30-day inactivity removal above. This persistence lasts for
            the life of your subscription: we do not remove your raw source photos due
            to age, and we do not apply any other absolute deletion deadline to them
            while you remain subscribed.
          </li>
        </ul>
        <p>
          <strong>What happens when you move to the Free plan.</strong> If a paid
          subscription is cancelled, lapses without being restored, or is downgraded to
          Free (see Section 6.7 for the failed-payment grace period and Section 6.8 for
          cancellation and downgrade timing), your account's entitlements become the
          Free plan's from that point forward. This does not place your content in a
          read-only state and does not carry any additional grace period. Your raw
          source photos are not deleted for exceeding the Free plan's pooled storage
          allowance: instead, further uploads are declined until you free up space or
          upgrade (see the Pooled storage allowance paragraph above), and your raw
          source photos become subject to the ordinary Free-plan retention policy in
          the bullets above. The compiled and edited videos produced from your photos
          are retained while the project exists. Except for the Free-plan warning
          email described above, removal is automatic and we are not obligated to
          notify you before it occurs. This Section works together with the
          data-retention practices described in Section 7 of our Privacy Policy and
          the subscription terms in Section 6.
        </p>

        <h2>5. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Upload content that is illegal, infringing, defamatory, obscene, or harmful to others</li>
          <li>Upload content containing the personal information of third parties without their consent</li>
          <li>Attempt to disrupt, reverse-engineer, decompile, or circumvent the Service or its security measures</li>
          <li>Use the Service to process content you do not have the right to use</li>
          <li>Use automated means to access the Service in a manner that exceeds reasonable use</li>
          <li>Resell, sublicense, or redistribute the Service or access thereto without our prior written permission</li>
          <li>Use the Service in violation of any applicable law or regulation</li>
        </ul>

        <h2>6. Credits and Payments</h2>

        <h3>6.1 Purchasing Credits</h3>
        <p>
          Certain processing features require credits. Credits reach your account in
          three ways: (a) one-time "top-up" credit packs, available only while you have
          an active paid subscription, that you purchase through the Service via
          Stripe; (b) monthly credit allotments included with a paid
          subscription plan (see Section 6.6); and (c) promotional or bonus credits.
          All purchases are final and non-refundable except as required by applicable
          law and except as expressly provided in Section 6.4 (Service Discontinuation
          and Credit Refunds). We reserve the right to change prices and credit costs
          with reasonable notice. Purchased top-up credits do not expire while the
          Service remains in operation, and have no cash value except as provided in
          Section 6.4. Monthly subscription allotment credits expire as described in
          Section 6.6, and promotional or bonus credits are subject to the additional
          terms disclosed at the time of the promotion.
        </p>

        <h3>6.2 Credit Consumption</h3>
        <p>
          Credits are consumed when you generate an edited video, and when you
          compile uploaded photos into a video (see Section 6.9 for photo compile
          pricing). For an edited video, the number of credits required depends on
          the duration of your original source video — not the length of the edited
          output — and the output resolution. Pricing is based on your source video
          so the cost is fixed and predictable as soon as your video finishes
          uploading, regardless of how much automatic stillness detection trims from
          the final edit:
        </p>
        <ul>
          <li>720p — 0.5 credits per minute of source video</li>
          <li>1080p — 1 credit per minute of source video</li>
          <li>4K — 3.5 credits per minute of source video</li>
        </ul>

        <h3>6.3 Rounding and Minimum Charge</h3>
        <p>
          Fractional credit amounts are rounded up to the nearest whole credit. Every
          video generation incurs a minimum charge of 1 credit, regardless of your
          source video's duration or the length of the edited output. An estimated
          credit cost, based on your source video's duration, is displayed before you
          confirm video generation.
        </p>

        <h3>6.4 Service Discontinuation and Credit Refunds</h3>
        <p>
          We may discontinue the Service, in whole or in part, at any time. If we
          permanently discontinue the paid video-generation features of the Service, we
          will:
        </p>
        <ol type="a">
          <li>
            give you at least thirty (30) days' advance notice by email to the address
            associated with your account and by notice within the Service;
          </li>
          <li>
            continue to honor your existing credit balance for video generation through
            the discontinuation date stated in that notice; and
          </li>
          <li>
            after the discontinuation date, refund the unused portion of any credits you
            purchased for cash, valued at the original purchase price you paid per
            credit, to your original payment method where reasonably practicable, or by
            another reasonable method if it is not.
          </li>
        </ol>
        <p>
          Promotional, bonus, and other credits not purchased for cash have no cash
          value and are not refundable. Where a credit pack was sold at a bundled or
          discounted rate, refunds are calculated on a pro-rata basis against the actual
          amount you paid. If we are unable to process a refund to your original payment
          method, you must provide alternative payment details within ninety (90) days
          of our notice to receive the refund. Our obligation to issue refunds under
          this Section 6.4 is not subject to the limitation of liability in Section 10.
          Except for our refund obligation under this Section 6.4, this Section states
          your sole and exclusive remedy, and our entire liability, for discontinuation
          of the Service.
        </p>

        <h3>6.5 Subscription Plans</h3>
        <p>
          We offer optional paid subscription plans billed monthly through Stripe. Each
          plan includes a pooled cloud storage allowance shared across your projects, a
          monthly credit allotment, unlimited projects, and watermark-free video
          exports. Our current founding plans are:
        </p>
        <ul>
          <li>
            <strong>Free — $0.</strong> 2 GB pooled storage, no monthly credit
            allotment (10 one-time starter credits on signup), unlimited projects, and
            exports that carry a Tweedia watermark.
          </li>
          <li>
            <strong>Solo — $15 / month.</strong> 50 GB pooled storage, 75 credits each
            month, unlimited projects, and watermark-free exports.
          </li>
          <li>
            <strong>Pro — $39 / month.</strong> 250 GB pooled storage, 250 credits each
            month, unlimited projects, and watermark-free exports.
          </li>
        </ul>
        <p>
          All plans can export at resolutions up to 4K; the Tweedia watermark, not
          resolution, is the difference between Free and paid exports. Subscriptions
          renew automatically each month until cancelled, and you authorize us to charge
          your payment method on file for each renewal. You may cancel at any time
          through your account settings or the Stripe customer portal, effective at the
          end of the then-current billing period. Storage allowances and watermark-free
          exports apply only while a subscription is active; see Sections 6.8 and 4.5
          for what happens on cancellation, downgrade, or lapse.
        </p>

        <h3>6.6 Monthly Credit Allotments</h3>
        <p>
          The credits included with a subscription plan are allotted at the start of
          each billing cycle and <strong>expire at the end of that billing cycle</strong>.
          Unused monthly allotment credits do not roll over to the next cycle and have no
          cash value. Because they are consumed within the cycle and are not sold for
          cash as a separate credit pack, monthly allotment credits are not separately
          refundable and are not covered by the refund obligation in Section 6.4.
        </p>
        <p>
          Purchased "top-up" credit packs — available only while you have an active
          subscription (see Section 6.1) — are separate from your monthly allotment:
          they do not expire while the Service remains in operation, they carry over
          from month to month, and they remain covered by the Service-discontinuation
          refund commitments in Section 6.4. When you generate a video, we draw down
          your expiring monthly allotment credits first, and then your non-expiring
          top-up credits, so that your purchased credits are preserved as long as
          possible.
        </p>

        <h3>6.7 Founding Pricing</h3>
        <p>
          Subscriptions started at a "founding" rate are price-protected: for as long as
          your subscription remains continuously active, the monthly price and the
          storage and credit allotments in effect at the time you subscribed will not
          increase, even if we raise prices or change allotments for new customers.
          Price protection applies only while the subscription is continuously active:
          if your subscription lapses (for example, due to a failed payment) and is not
          restored within a 60-day grace period, or if you cancel and later
          re-subscribe, your founding rate ends and the current public price for new
          subscribers applies to your next subscription. Your founding storage and
          credit allotments work differently: they are tied to your account, not to a
          single continuously active subscription, so a lapse, a cancellation, or a
          later re-subscription never resets them — your original founding allotments
          are restored, at minimum, once paid access resumes, even if we have since
          changed allotments for new customers. We reserve the right to change pricing,
          plan features, and credit allotments for new customers at any time, with
          reasonable notice.
        </p>

        <h3>6.8 Plan Changes, Downgrades, and Cancellation</h3>
        <p>
          You may upgrade, downgrade to another paid plan, or cancel your subscription
          at any time through your account settings or the Stripe customer portal.
          Switching between paid plans — upgrading or downgrading between Solo and Pro —
          takes effect immediately, with a prorated adjustment to your bill reflecting
          the price difference for the remainder of the current billing cycle.
          Cancelling (which includes downgrading to the Free plan, since Free has no
          separate subscription to switch to) takes effect at the end of the current
          billing cycle: you keep your plan's storage, credits, and watermark-free
          exports through the period you already paid for, and your account moves to
          the Free plan when that period ends.
        </p>
        <p>
          Moving to the Free plan — whether by cancelling, letting a subscription lapse,
          or a downgrade — does not place your content in a read-only state and does not
          carry a grace period. If your stored content then exceeds the Free plan's
          pooled storage allowance, it is not deleted for that reason: further uploads
          are declined until you free up space or upgrade, and your raw source photos
          become subject to the Free plan's ordinary retention policy in Section 4.5.
          Your compiled videos and account remain subject to the general retention terms
          in Section 4.5 and our Privacy Policy.
        </p>

        <h3>6.9 Photo Compile Pricing</h3>
        <p>
          Compiling the photos you upload into a single video (a "photo compile") is a
          separate paid step from generating an edited video (Section 6.2). Credits are
          consumed based on the duration of the compiled video — the number of photos
          you select divided by the frame rate you choose — and on the output
          resolution you choose: 1080p, the default, costs 1 credit per minute of
          compiled video, and 4K costs 3.5 credits per minute. Compiles are offered at
          those two resolutions only. The same rounding and 1-credit minimum described
          in Section 6.3 apply to that cost calculation, before the re-compile credit
          described below is applied, and the credit cost of a compile is shown to you
          before you start it.
        </p>
        <p>
          If you re-compile the same project, you are charged only the amount by which
          the new compile costs more than the compile credits you have already paid for
          that project. Re-compiling something you have already paid for is therefore
          free, an upgrade costs only the difference, and you are never charged twice
          for the same output. We do not refund the difference when you re-compile at a
          cheaper setting: those credits stay applied to that project and continue to
          count toward any later compile of it.
        </p>
        <p>
          If a compile fails, the credits charged for that attempt are automatically
          refunded to your credit balance. If a compile is interrupted without reporting
          a failure, the credits charged for it stay applied to that project. You may
          need to wait a short time for the interrupted attempt to time out before you
          can start another compile of that project; once it does, re-running the
          compile costs you nothing further. The credit costs described
          in Sections 6.2 and 6.3 apply separately to edited-video renders: generating
          an edited video from a compiled result is charged on its own, and the credits
          you paid to compile do not cover it.
        </p>

        <h2>7. Intellectual Property</h2>
        <p>
          The Service and its original content (excluding User Content), features,
          and functionality are and will remain the exclusive property of Tweedia
          Technologies LLC and its licensors. The Service is protected by copyright,
          trademark, and other laws. Our trademarks and trade dress may not be used
          in connection with any product or service without our prior written consent.
        </p>

        <h2>8. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time, with or
          without notice, for conduct that we reasonably believe violates these Terms,
          is harmful to the Service or other users, or for any other reason at our
          sole discretion. Upon termination, your right to use the Service ceases
          immediately. Sections 4.3, 4.4, 6.4, 7, 9, 10, 11, 12, and 13 survive termination.
        </p>

        <h2>9. Disclaimer of Warranties</h2>
        <p className="legal-caps">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
          ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT
          NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. TWEEDIA DOES NOT WARRANT
          THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR THAT ANY
          DEFECTS WILL BE CORRECTED. TWEEDIA MAKES NO WARRANTY REGARDING THE QUALITY,
          ACCURACY, TIMELINESS, OR COMPLETENESS OF ANY RESULTS OBTAINED THROUGH THE
          SERVICE.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p className="legal-caps">
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL TWEEDIA
          TECHNOLOGIES LLC, ITS OFFICERS, DIRECTORS, MEMBERS, EMPLOYEES, AGENTS, OR
          LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR
          LOSS OF PROFITS, GOODWILL, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT
          OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS
          OR USE) THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
          NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, EVEN IF TWEEDIA HAS BEEN
          ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p className="legal-caps">
          IN NO EVENT SHALL TWEEDIA'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS
          ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE EXCEED THE GREATER
          OF (A) FIFTY U.S. DOLLARS ($50.00) OR (B) THE TOTAL AMOUNT YOU HAVE PAID
          TO TWEEDIA IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING
          RISE TO THE CLAIM. THE FOREGOING LIMITATION DOES NOT APPLY TO OUR OBLIGATION
          TO REFUND PURCHASED CREDITS UNDER SECTION 6.4 (SERVICE DISCONTINUATION AND
          CREDIT REFUNDS).
        </p>

        <h2>11. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Tweedia Technologies LLC and its
          officers, directors, members, employees, and agents from and against any
          and all claims, liabilities, damages, losses, costs, and expenses (including
          reasonable attorneys' fees) arising out of or in connection with: (a) your
          use of the Service; (b) your User Content; (c) your violation of these
          Terms; or (d) your violation of any rights of a third party.
        </p>

        <h2>12. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws
          of the State of Texas, United States, without regard to its conflict of
          law principles. You and Tweedia agree that the exclusive venue and
          jurisdiction for any dispute arising out of or relating to these Terms or
          the Service shall be the state and federal courts located in{' '}
          Travis County, Texas, and you
          consent to personal jurisdiction in those courts. Nothing in this section
          limits any right that you have under the Texas Deceptive Trade Practices
          Act (Tex. Bus. &amp; Com. Code Ch. 17) or other non-waivable consumer
          protection law applicable to you.
        </p>
        <p>
          Before filing any formal legal proceeding, you agree to first attempt to
          resolve any dispute informally by contacting us at{' '}
          <a href="mailto:support@tweediastudio.com">support@tweediastudio.com</a>. If the
          dispute is not resolved within thirty (30) days, either party may proceed
          with formal proceedings.
        </p>
        <p>
          <strong>Class Action Waiver:</strong> To the fullest extent permitted by
          applicable law, you and Tweedia each agree that any dispute resolution
          proceedings will be conducted only on an individual basis and not in a
          class, consolidated, or representative action. If this class action waiver
          is found to be unenforceable, then the entirety of this dispute resolution
          section shall be null and void.
        </p>

        <h2>13. General Provisions</h2>
        <ul>
          <li>
            <strong>Severability:</strong> If any provision of these Terms is found to
            be unenforceable, the remaining provisions will continue in full force and
            effect.
          </li>
          <li>
            <strong>Entire Agreement:</strong> These Terms, together with our Privacy
            Policy, constitute the entire agreement between you and Tweedia regarding
            the Service and supersede all prior agreements.
          </li>
          <li>
            <strong>Assignment:</strong> You may not assign or transfer these Terms
            without our prior written consent. We may assign these Terms without
            restriction.
          </li>
          <li>
            <strong>Waiver:</strong> Our failure to enforce any provision of these
            Terms shall not constitute a waiver of that provision.
          </li>
          <li>
            <strong>Force Majeure:</strong> We shall not be liable for any failure or
            delay in performance due to circumstances beyond our reasonable control,
            including but not limited to acts of God, natural disasters, war,
            terrorism, labor disputes, government actions, or internet or
            infrastructure failures.
          </li>
        </ul>

        <h2>14. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. If we make material changes,
          we will notify you by posting the updated Terms on the Service with a new
          "Last updated" date, and where required by law, by email or other notice.
          Material changes may require you to re-accept these Terms through an in-app
          prompt before you can continue using the Service. If you do not accept the
          revised Terms when prompted, you will not be able to use account-protected
          features until you do so.
        </p>

        <h2>15. Contact</h2>
        <p>
          Questions about these Terms may be directed to:
        </p>
        <p>
          Tweedia Technologies LLC<br />
          5900 Balcones Drive STE 100<br />
          Austin, Texas 78731<br />
          Email: <a href="mailto:support@tweediastudio.com">support@tweediastudio.com</a>
        </p>
      </main>
      <Footer />
    </div>
  );
}

export default TermsOfService;
